import { useLazyQuery } from "@apollo/client/react";
import { GetMyStudyProgrammes } from "__generated__/types";
import { signInWithRedirect } from "aws-amplify/auth";
import { SyllabusIcon } from "common/assets/icons/SyllabusIcon";
import { useAuth } from "common/auth/Auth";
import { Button } from "common/buttons";
import { Box } from "common/components/Box";
import { CenteredSimpleLayout } from "common/components/layouts/CenteredSimpleLayout";
import { DefaultLayout } from "common/components/layouts/DefaultLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { LinkBox } from "common/components/links/LinkBox";
import { LinkButton } from "common/components/links/LinkButton";
import { Loading } from "common/components/Loading";
import { Title } from "common/components/Title";
import { H1, Paragraph } from "common/components/Typography";
import {
  GET_MY_STUDYPROGRAMMES,
  StudyProgrammesActionTypes,
  useMyStudyProgrammes,
} from "common/context/MyStudyProgrammesContext";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { urls } from "common/urls";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./FrontPage.module.css";

const Page = ({ name = "ukjent" }: { name?: string | null }) => {
  const { state: myStudyProgrammes, dispatch } = useMyStudyProgrammes();
  const { myExamSyllabi } = useMyExamSyllabus();

  const [showSelector, setShowSelector] = useState<boolean>(true);

  const [initialExamSyllabiCount, setInitialExamSyllabiCount] =
    useState<number>();

  const [, { data: studyProgrammesData }] = useLazyQuery<GetMyStudyProgrammes>(
    GET_MY_STUDYPROGRAMMES,
  );

  useEffect(() => {
    if (studyProgrammesData !== undefined) {
      dispatch({
        type: StudyProgrammesActionTypes.SetData,
        payload: {
          myStudyProgrammes: studyProgrammesData.studyProgrammeList,
        },
      });
    }
  }, [studyProgrammesData]);

  useEffect(() => {
    if (myExamSyllabi && initialExamSyllabiCount === undefined) {
      setInitialExamSyllabiCount(myExamSyllabi.length);
    }
  }, [myExamSyllabi]);

  const selector = useMemo(() => {
    const noStudyProgrammes =
      !myStudyProgrammes || myStudyProgrammes.studyProgrammes.length === 0;
    const noExamSyllabi = initialExamSyllabiCount === 0 && !!myStudyProgrammes;

    if (noStudyProgrammes) {
      return (
        <div className={styles.links}>
          <LinkBox
            title="Pensum"
            icon={<SyllabusIcon withBackground />}
            content={
              <Paragraph>
                Du har ikke valgt studier ennå. Velg studier og pensum for å
                komme i gang.
              </Paragraph>
            }
            linkText="Legg til pensum og studie"
            to={urls.profileStudies}
          />
          <LinkButton to={urls.library}>Utforsk biblioteket</LinkButton>
        </div>
      );
    } else if (noExamSyllabi) {
      return (
        <div className={styles.links}>
          <LinkBox
            title="Pensum"
            icon={<SyllabusIcon withBackground />}
            content={
              <Paragraph>
                Du har ikke valgt pensum for dine studier ennå. Klikk her for å
                legge til.
              </Paragraph>
            }
            linkText="Legg til pensum"
            to={urls.profileStudies}
          />
          <LinkButton to={urls.library}>Utforsk biblioteket</LinkButton>
        </div>
      );
    }

    return null;
  }, [myStudyProgrammes, initialExamSyllabiCount]);

  return (
    <NormalContentLayout title="" breadcrumbItems={[]}>
      <div className={styles.container}>
        <H1>Velkommen, {name}!</H1>
        <section className={styles.introductionText}>
          <Paragraph>
            I Adapt kan du jobbe med pensum for å lære deg materialet til
            eksamen, eller utforske alt innholdet i biblioteket dersom du er
            nysgjerrig. Dersom du for eksempel vil legge til flere eksamener du
            skal ta, kan du gjøre det inne på din profil.
          </Paragraph>
        </section>
        {showSelector && selector ? (
          <Box
            centeredContent={false}
            onClose={() => {
              setShowSelector(false);
            }}
          >
            {selector}
          </Box>
        ) : (
          <div className={styles.links}>
            <LinkBox
              title="Pensum"
              icon={<SyllabusIcon withBackground />}
              content={
                <Paragraph>
                  Inne på ditt pensum kan du se alt av undervisningsmateriell
                  det er tenkt at du skal arbeide deg gjennom.
                </Paragraph>
              }
              linkText="Jobb med pensum"
              to={
                myExamSyllabi?.length === 1
                  ? urls.exam(myExamSyllabi[0].id)
                  : urls.myStudies
              }
            />
            <LinkButton to={urls.library}>Utforsk biblioteket</LinkButton>
            <LinkButton to={urls.profileStudies}>
              Legg til pensum og studie
            </LinkButton>
          </div>
        )}
      </div>
    </NormalContentLayout>
  );
};

export const FrontPage: React.FC = () => {
  const auth = useAuth();

  if (auth.loading) {
    return (
      <LoadingLayout>
        <Loading />
      </LoadingLayout>
    );
  }

  if (!auth.isLoggedIn) {
    return (
      <DefaultLayout showNavBar={false}>
        <CenteredSimpleLayout>
          <div style={{ textAlign: "center" }}>
            <Title withSpacing>Velkommen til Adapt</Title>
            <Button
              kind="primary"
              onClick={() =>
                signInWithRedirect(/*
                  // Uncomment this to skip cognito login page
                  {customProvider: "Feide",}
                */)
              }
            >
              Logg inn
            </Button>
          </div>
        </CenteredSimpleLayout>
      </DefaultLayout>
    );
  }

  return <Page name={auth.me?.principal?.user.fullName} />;
};
